import { useCallback, useMemo, useState } from "react";
import { useGetLayoutDefQuery, useUpdateLayoutDefMutation } from "store/api";

export type WidgetElement = {
  i: string; // unique ID
  x: number;
  y: number;
  w: number;
  h: number;
  widget: Record<string, any>;
};

type TableWidgetField = {
  field: string;
  display: boolean;
  edit: boolean;
  link: boolean;
  file: boolean;
  linkValue: string;
  linkField: string;
  linkOwnField: string;
};

const createDefaultTableField = (fieldName: string): TableWidgetField => ({
  field: fieldName,
  display: fieldName !== "id",
  edit: fieldName !== "id",
  link: false,
  file: false,
  linkValue: "",
  linkField: "",
  linkOwnField: fieldName,
});

const toBooleanFlag = (value: unknown, defaultValue: boolean) => {
  if (value === undefined || value === null) return defaultValue;
  if (value === true || value === "true" || value === 1 || value === "1") return true;
  if (value === false || value === "false" || value === 0 || value === "0") return false;
  return defaultValue;
};

const normalizeTableWidgetFields = (widgetData: Record<string, any>) => {
  if (widgetData?.type !== "TableWidget") return widgetData;

  const rawFields = Array.isArray(widgetData.fields) ? widgetData.fields : [];
  const normalizedFields = rawFields
    .map((entry) => {
      if (typeof entry === "string") {
        return createDefaultTableField(entry);
      }

      if (entry && typeof entry === "object") {
        const record = entry as Record<string, any>;
        const fieldName = String(record.field ?? "").trim();
        if (!fieldName) return null;

        const display = toBooleanFlag(record.display, true);
        const link = toBooleanFlag(record.link, false);
        const file = toBooleanFlag(record.file, false);
        const edit = display ? toBooleanFlag(record.edit, true) : false;

        return {
          field: fieldName,
          display,
          edit,
          link,
          file: link ? false : file,
          linkValue: String(record.linkValue ?? ""),
          linkField: String(record.linkField ?? ""),
          linkOwnField: String(record.linkOwnField ?? fieldName),
        } satisfies TableWidgetField;
      }

      return null;
    })
    .filter((value): value is TableWidgetField => value != null);

  return {
    ...widgetData,
    fields: normalizedFields,
  };
};

/**
 * Hook for managing widget form submissions in layout editor.
 * Handles adding/updating widgets and persisting to backend.
 */
export const useWidgetSubmit = (layoutId: string | number | undefined) => {
  const { data: layoutDef } = useGetLayoutDefQuery(layoutId ?? "", {
    skip: !layoutId,
    refetchOnMountOrArgChange: true,
  });
  const [updateLayoutDef] = useUpdateLayoutDefMutation();
  const [layout, setLayout] = useState<WidgetElement[] | null>(null);

  const currentLayout = useMemo(() => {
    if (layout) {
      return layout;
    }

    const persistedLayout = (layoutDef as { layout?: WidgetElement[] } | null)?.layout;
    return Array.isArray(persistedLayout) ? persistedLayout : [];
  }, [layout, layoutDef]);

  /**
   * Add a new widget or update existing widget in the layout.
   * Follows old project pattern: generate ID, position, size based on type.
   */
  const handleSubmit = useCallback(
    async (widgetData: Record<string, any>, editWidgetId?: string) => {
      if (!layoutId) {
        console.error("Layout ID not provided");
        return;
      }

      try {
        const safeWidgetData = normalizeTableWidgetFields(widgetData);
        let newLayout = [...currentLayout];

        if (editWidgetId) {
          // Update existing widget without mutating RTK Query cached objects.
          newLayout = newLayout.map((element) =>
            element.i === editWidgetId
              ? {
                  ...element,
                  widget: safeWidgetData,
                }
              : element
          );
        } else {
          // Add new widget
          // Size: BasicWidget=2 cols, others=6 cols (from old project)
          const size = safeWidgetData.type === "BasicWidget" ? 2 : 6;
          const length = newLayout.length;
          const x = (length * size) % 12;
          const y = 0;

          const newElement: WidgetElement = {
            i: `n${Date.now()}`, // unique ID
            x: x === 0 ? 0 : x,
            y,
            w: size,
            h: size,
            widget: safeWidgetData,
          };

          newLayout.push(newElement);
        }

        setLayout(newLayout);

        // Save to backend
        await updateLayoutDef({
          id: layoutId,
          layout: newLayout,
        }).unwrap();

        console.debug("Widget saved successfully", safeWidgetData);
      } catch (error) {
        console.error("Failed to save widget:", error);
        throw error;
      }
    },
    [currentLayout, layoutId, updateLayoutDef]
  );

  /**
   * Remove widget from layout
   */
  const handleRemove = useCallback(
    async (widgetId: string) => {
      if (!layoutId) return;

      try {
        const newLayout = currentLayout.filter((el) => el.i !== widgetId);
        setLayout(newLayout);

        await updateLayoutDef({
          id: layoutId,
          layout: newLayout,
        }).unwrap();
      } catch (error) {
        console.error("Failed to remove widget:", error);
        throw error;
      }
    },
    [currentLayout, layoutId, updateLayoutDef]
  );

  return {
    layout,
    setLayout,
    handleSubmit,
    handleRemove,
  };
};
